import { useCallback, useEffect, useRef } from 'react';
import { Canvas, FabricImage, FabricObject } from 'fabric';
import { FRAGMENT_SHADER, AlphaFalloffParams, DEFAULT_ALPHA_FALLOFF_PARAMS } from '../constants/alpha-falloff-shader';
import { VERTEX_SHADER } from '../constants';
import { compileShader, createProgram } from '../utils/shaders-utils';
import { rasterizeFabricObject, Bbox } from '../../media/utils/rasterize-fabric-object';
import { analyzeAlphaContent } from '../utils/analyze-alpha-content';
import { findByPieceId, PIECE } from '../../canvas/utils/canvas-pieces';
import { onModelRendered } from '../../canvas/canvas-events';
import { usePieceDispatch } from '../../model/piece-context';
import type { LayerId } from '../../model/types';

/**
 * Alpha falloff — "overlay-baked" model.
 *
 * Enabling the effect adds a `FabricImage` overlay on top of the subject.
 * The overlay holds background pixels masked to a soft-edged band over the
 * subject's bottom, so the background appears to "fog" over its hard
 * cut-edge — without touching the subject's own pixels.
 *
 * Subject-anchored on fresh enable: every time the effect is turned on
 * from off, `center` and `waist` are auto-fit from the subject's current
 * alpha bbox, so the fog tracks the subject's position. Slider edits made
 * while the effect is on go through `rebake` and are NOT overridden —
 * the user stays in control of any value they've dialed in. Moving the
 * subject without toggling does not re-fit (the overlay is just an image).
 *
 * Source of truth: the model (`content.alphaFalloff`, on an image, video or group).
 * `applyEffects` projects it onto the Fabric object as `alphaFalloffEnabled` /
 * `alphaFalloffParams`, which also serialise into the canvas JSON for export.
 * The overlay (`pieceId='alpha-falloff'`) is derived — pixels, not a layer.
 * `reconcile` re-bakes/removes it from those flags after the model repaints.
 */

type Renderer = {
  canvas: HTMLCanvasElement;
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  sourceTex: WebGLTexture;
  vao: WebGLVertexArrayObject;
  uniforms: Record<string, WebGLUniformLocation | null>;
  designWidth: number;
  designHeight: number;
  /** Supersample factor: GL canvas / textures are `designSize * scale` to
   *  match the live Fabric canvas's Retina-aware rendering quality. */
  scale: number;
};

/** Hard cap to stay within `MAX_TEXTURE_SIZE` on older desktop GPUs. */
const MAX_RASTER_SIZE = 8192;

/** Lateral smooth half-width as a fraction of the band width. */
const BAND_X_SMOOTH_RATIO = 0.04;

function supersampleFactor(designWidth: number, designHeight: number): number {
  const dpr = window.devicePixelRatio || 1;
  const cap = MAX_RASTER_SIZE / Math.max(designWidth, designHeight);
  return Math.max(1, Math.min(dpr, cap));
}

function createRenderer(designWidth: number, designHeight: number, scale: number): Renderer | null {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(designWidth * scale);
  canvas.height = Math.round(designHeight * scale);
  const gl = canvas.getContext('webgl2', { premultipliedAlpha: false, alpha: true });
  if (!gl) return null;

  const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = createProgram(gl, vs, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  gl.useProgram(program);

  const sourceTex = gl.createTexture()!;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, sourceTex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.uniform1i(gl.getUniformLocation(program, 'u_source'), 0);

  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  const uniforms = {
    bandX: gl.getUniformLocation(program, 'u_bandX'),
    bandXSmooth: gl.getUniformLocation(program, 'u_bandXSmooth'),
    ceiling: gl.getUniformLocation(program, 'u_ceiling'),
    floor: gl.getUniformLocation(program, 'u_floor'),
    bandHeightRatio: gl.getUniformLocation(program, 'u_bandHeightRatio'),
    waistSmooth: gl.getUniformLocation(program, 'u_waistSmooth'),
    waistCurvature: gl.getUniformLocation(program, 'u_waistCurvature'),
    bboxOrigin: gl.getUniformLocation(program, 'u_bboxOrigin'),
    bboxSize: gl.getUniformLocation(program, 'u_bboxSize')
  };

  return { canvas, gl, program, sourceTex, vao, uniforms, designWidth, designHeight, scale };
}

function disposeRenderer(r: Renderer) {
  r.gl.deleteTexture(r.sourceTex);
  r.gl.deleteVertexArray(r.vao);
  r.gl.deleteProgram(r.program);
}

function shaderRender(r: Renderer, source: HTMLCanvasElement, params: AlphaFalloffParams, bbox: Bbox) {
  const { gl, uniforms } = r;

  // GL canvas + viewport follow the source's pixel size.
  if (r.canvas.width !== source.width || r.canvas.height !== source.height) {
    r.canvas.width = source.width;
    r.canvas.height = source.height;
  }
  gl.viewport(0, 0, source.width, source.height);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, r.sourceTex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

  gl.uniform2f(uniforms.bandX, params.bandX[0], params.bandX[1]);
  gl.uniform1f(uniforms.bandXSmooth, params.bandXSmooth);
  gl.uniform1f(uniforms.ceiling, params.ceiling);
  gl.uniform1f(uniforms.floor, params.floor);
  gl.uniform1f(uniforms.bandHeightRatio, params.bandHeightRatio);
  gl.uniform1f(uniforms.waistSmooth, params.waistSmooth);
  gl.uniform1f(uniforms.waistCurvature, params.waistCurvature);
  gl.uniform2f(uniforms.bboxOrigin, bbox.left / r.designWidth, bbox.top / r.designHeight);
  gl.uniform2f(uniforms.bboxSize, bbox.width / r.designWidth, bbox.height / r.designHeight);

  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}

// The GL backing buffer is reused across passes, so we hand the FabricImage
// a stable 2D copy — otherwise the next render would visibly stomp it.
function snapshotGL(r: Renderer): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = r.canvas.width;
  out.height = r.canvas.height;
  const ctx = out.getContext('2d');
  if (ctx) ctx.drawImage(r.canvas, 0, 0);
  return out;
}

/**
 * Auto-fit the band's geometry to the subject's alpha bbox: `bandX` to its
 * width, `ceiling` / `floor` to its vertical extent. Called on every fresh
 * enable so the effect tracks the subject's current position. Style fields
 * (bandHeightRatio, smoothing, curvature) pass through untouched.
 */
function fitParamsToSubject(params: AlphaFalloffParams, subject: FabricObject, renderer: Renderer): AlphaFalloffParams {
  const rect = subject.getBoundingRect();
  const bbox: Bbox = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  const subjectCanvas = rasterizeFabricObject(subject, bbox, renderer.scale);
  const analysis = analyzeAlphaContent(subjectCanvas);
  if (!analysis.bbox) return params;

  const toCanvasX = (u: number) => (bbox.left + u * bbox.width) / renderer.designWidth;
  const toCanvasY = (v: number) => (bbox.top + v * bbox.height) / renderer.designHeight;

  const x1 = toCanvasX(analysis.bbox.minX);
  const x2 = toCanvasX(analysis.bbox.maxX);
  return {
    ...params,
    bandX: [x1, x2],
    bandXSmooth: (x2 - x1) * BAND_X_SMOOTH_RATIO,
    ceiling: toCanvasY(analysis.bbox.minY),
    floor: toCanvasY(analysis.bbox.maxY)
  };
}

/** Rasterise the background at canvas dimensions and run the shader on it.
 *  Returns the resulting overlay snapshot, or `null` if no background. */
function bakeOverlaySnapshot(canvas: Canvas, renderer: Renderer, params: AlphaFalloffParams): HTMLCanvasElement | null {
  const bg = findByPieceId(canvas, PIECE.BACKGROUND);
  if (!bg) return null;

  const designBbox: Bbox = {
    left: 0,
    top: 0,
    width: renderer.designWidth,
    height: renderer.designHeight
  };
  const bgRaster = rasterizeFabricObject(bg, designBbox, renderer.scale);
  shaderRender(renderer, bgRaster, params, designBbox);
  return snapshotGL(renderer);
}

/** Build a FabricImage suitable for use as the alpha-falloff overlay. */
function createOverlay(snapshot: HTMLCanvasElement, renderer: Renderer): FabricImage {
  // Element is `designSize * scale` physical pixels; inverse scale brings the
  // overlay to canvas world-coords so Fabric maps 1:1 on a DPR-aware canvas.
  const img = new FabricImage(snapshot, {
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    scaleX: 1 / renderer.scale,
    scaleY: 1 / renderer.scale,
    selectable: false,
    evented: false,
    hasControls: false,
    objectCaching: false
  });
  img.pieceId = PIECE.ALPHA_FALLOFF;
  // Filtered from the layers panel (see `isSystemPiece`); persists in JSON.
  img.isOverlay = true;
  return img;
}

function placeAboveSubject(canvas: Canvas, overlay: FabricObject, subject: FabricObject) {
  const subjectIndex = canvas.getObjects().indexOf(subject);
  canvas.moveObjectTo(overlay, subjectIndex + 1);
}

/** Replace any existing overlay with a fresh one over the subject. */
function bakeFresh(
  canvas: Canvas,
  subject: FabricObject,
  params: AlphaFalloffParams,
  renderer: Renderer
): AlphaFalloffParams {
  // Fresh enable always re-fits to the subject's current position. Stale
  // center/waist from a previous bake (e.g. user moved the subject and
  // re-toggled) get overridden so the fog tracks the subject.
  const effective = fitParamsToSubject(params, subject, renderer);
  const snapshot = bakeOverlaySnapshot(canvas, renderer, effective);
  if (!snapshot) return effective;

  const stale = findByPieceId(canvas, PIECE.ALPHA_FALLOFF);
  if (stale) canvas.remove(stale);

  const overlay = createOverlay(snapshot, renderer);
  canvas.add(overlay);
  placeAboveSubject(canvas, overlay, subject);

  subject.alphaFalloffEnabled = true;
  subject.alphaFalloffParams = effective;
  canvas.requestRenderAll();
  return effective;
}

/** Refresh the existing overlay's pixels with new params. */
function rebake(
  canvas: Canvas,
  subject: FabricObject,
  params: AlphaFalloffParams,
  renderer: Renderer
): AlphaFalloffParams {
  const existing = findByPieceId(canvas, PIECE.ALPHA_FALLOFF) as FabricImage | null;
  if (!existing) return bakeFresh(canvas, subject, params, renderer);

  // Rebake honours slider values verbatim — no re-fit so the user keeps
  // control of `center` / `waist` once they've touched them.
  const effective = params;
  const snapshot = bakeOverlaySnapshot(canvas, renderer, effective);
  if (!snapshot) return effective;

  existing.setElement(snapshot);
  existing.dirty = true;
  placeAboveSubject(canvas, existing, subject);

  subject.alphaFalloffParams = effective;
  canvas.requestRenderAll();
  return effective;
}

/** Remove the overlay; subject's params stay so re-enable picks up where it was. */
function restoreOriginal(canvas: Canvas, subject: FabricObject) {
  const overlay = findByPieceId(canvas, PIECE.ALPHA_FALLOFF);
  if (overlay) {
    canvas.remove(overlay);
    canvas.requestRenderAll();
  }
  subject.alphaFalloffEnabled = false;
}

type Transaction = (fn: () => void | Promise<void>) => Promise<void>;

type UseAlphaFalloffReturn = {
  /**
   * Applies the effect to the canvas and returns the params used.
   * Live previews can skip the model commit so slider drags become a single undo step.
   */
  setSubjectEffect: (
    subject: FabricObject,
    enabled: boolean,
    params: AlphaFalloffParams,
    commit?: boolean
  ) => Promise<AlphaFalloffParams>;

  /** Syncs the overlay with the objects that have the effect enabled. */
  reconcile: () => void;
};

export const useAlphaFalloff = (
  canvas: Canvas | null,
  designWidth: number,
  designHeight: number,
  transaction?: Transaction
): UseAlphaFalloffReturn => {
  const rendererRef = useRef<Renderer | null>(null);
  const dispatch = usePieceDispatch();

  useEffect(() => {
    const scale = supersampleFactor(designWidth, designHeight);
    rendererRef.current = createRenderer(designWidth, designHeight, scale);
    return () => {
      if (rendererRef.current) disposeRenderer(rendererRef.current);
      rendererRef.current = null;
    };
  }, [designWidth, designHeight]);

  const setSubjectEffect = useCallback<UseAlphaFalloffReturn['setSubjectEffect']>(
    async (subject, enabled, params, commit = true) => {
      if (!canvas || !rendererRef.current) return params;
      const isActive = subject.alphaFalloffEnabled === true;
      const sameParams = JSON.stringify(subject.alphaFalloffParams) === JSON.stringify(params);
      // Already baked exactly like this — a commit still needs to record it in the model.
      const baked = enabled === isActive && (!enabled || sameParams);

      let effective = enabled ? ((subject.alphaFalloffParams as AlphaFalloffParams | undefined) ?? params) : params;
      const disabled: FabricObject[] = [];

      if (!baked) {
        // Single-subject semantics: enabling on one disables the others.
        if (enabled) {
          for (const obj of canvas.getObjects()) {
            if (obj !== subject && obj.alphaFalloffEnabled === true) {
              obj.alphaFalloffEnabled = false;
              disabled.push(obj);
            }
          }
        }
        const r = rendererRef.current;
        if (enabled && !isActive) effective = bakeFresh(canvas, subject, params, r);
        else if (enabled && isActive) effective = rebake(canvas, subject, params, r);
        else if (!enabled && isActive) restoreOriginal(canvas, subject);
      }

      if (!commit) return effective; // live preview: canvas only, model untouched

      const commitTo = (obj: FabricObject, alphaFalloff: AlphaFalloffParams | undefined) => {
        const id = obj.layerId as LayerId | undefined;
        if (id) dispatch({ type: 'patchLayerContent', id, patch: { alphaFalloff } });
      };
      const run = () => {
        disabled.forEach(obj => commitTo(obj, undefined));
        commitTo(subject, enabled ? effective : undefined);
      };

      if (transaction) await transaction(run);
      else run();

      return effective;
    },
    [canvas, transaction, dispatch]
  );

  // Rebuilds or removes the overlay based on the objects that have the effect enabled.
  const reconcile = useCallback(() => {
    const renderer = rendererRef.current;
    if (!canvas || !renderer) return;
    const subject = canvas.getObjects().find(o => o.alphaFalloffEnabled === true && o.alphaFalloffParams) ?? null;
    const overlay = findByPieceId(canvas, PIECE.ALPHA_FALLOFF);
    if (subject) {
      if (!overlay) {
        bakeFresh(canvas, subject, subject.alphaFalloffParams as AlphaFalloffParams, renderer);
      } else {
        // The overlay isn't a model layer, so the reconciler's z-order pass can leave it
        // below the subject; keep it pinned directly above.
        placeAboveSubject(canvas, overlay, subject);
        canvas.requestRenderAll();
      }
    } else if (overlay) {
      canvas.remove(overlay);
      canvas.requestRenderAll();
    }
  }, [canvas]);

  // Rebuild the overlay after undo/redo updates the canvas.
  useEffect(() => {
    if (!canvas) return;
    return onModelRendered(canvas, reconcile);
  }, [canvas, reconcile]);

  return { setSubjectEffect, reconcile };
};

export { DEFAULT_ALPHA_FALLOFF_PARAMS };
export type { AlphaFalloffParams };
